import { useQuery } from "@tanstack/react-query";
import { Navigate, useNavigate } from "react-router-dom";

import { getSession } from "../lib/auth";
import { CreateOrgForm } from "../components/CreateOrgForm";

/**
 * "Create another organization" for an operator who already owns one.
 *
 * Distinct from `/welcome` (the zero-membership first-run screen): this one
 * does NOT bounce when the operator already has a provider — that's the whole
 * point. Reachable from the org switcher. The backend permits it (RBAC allows
 * an existing member to create more; team_member is one-to-many); on success
 * the server sets the new org active and we drop into its onboarding wizard.
 *
 *   - No session → /login.
 */
export function NewOrganization() {
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: ["session"],
    queryFn: getSession,
    retry: false,
    staleTime: 60_000,
  });

  if (isLoading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "var(--ad-fg-3)",
          fontSize: 13,
        }}
      >
        Loading…
      </div>
    );
  }

  if (!data?.user) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 480,
          padding: 32,
          background: "var(--ad-surface-1)",
          color: "var(--ad-fg-1)",
          border: "1px solid var(--ad-hairline)",
          borderRadius: 12,
          boxShadow: "var(--ad-card-shadow)",
        }}
      >
        <div
          style={{
            fontSize: 11,
            textTransform: "uppercase",
            letterSpacing: 0.6,
            color: "var(--ad-fg-3)",
            marginBottom: 6,
          }}
        >
          New organization
        </div>
        <h1 style={{ fontSize: 22, fontWeight: 600, margin: "0 0 8px" }}>
          Create a new organization
        </h1>
        <p style={{ fontSize: 14, color: "var(--ad-fg-3)", margin: "0 0 20px" }}>
          You'll be its owner. Your existing organizations are unaffected — you
          can switch between them any time from the sidebar.
        </p>

        <CreateOrgForm
          submitLabel="Create organization"
          onSuccess={() => navigate("/onboarding/survey", { replace: true })}
        />

        <div
          style={{
            marginTop: 20,
            paddingTop: 16,
            borderTop: "1px solid var(--ad-hairline)",
            fontSize: 12,
            color: "var(--ad-fg-3)",
          }}
        >
          <button
            type="button"
            onClick={() => navigate("/", { replace: true })}
            style={{
              background: "transparent",
              border: 0,
              padding: 0,
              color: "var(--rb-orange)",
              cursor: "pointer",
              fontSize: 12,
            }}
          >
            ← Back to dashboard
          </button>
        </div>
      </div>
    </div>
  );
}
